import { useEffect, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Loader2, MailX } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { api } from '@/lib/api-client';
import { isAuthenticated } from '@/lib/auth-store';

interface VerifySearch {
  token?: string;
}

export const Route = createFileRoute('/verify-email')({
  validateSearch: (s: Record<string, unknown>): VerifySearch => ({
    token: typeof s.token === 'string' ? s.token : undefined,
  }),
  component: VerifyEmailPage,
});

type Status = 'pending' | 'success' | 'error';

// A verification token is single-use, and React StrictMode double-mounts
// components in dev. Caching the in-flight promise per token guarantees exactly
// one API call and lets a remounted instance attach to the same result instead
// of firing a second (now-invalid) request.
const inflight = new Map<string, Promise<void>>();

function verifyOnce(token: string): Promise<void> {
  let p = inflight.get(token);
  if (!p) {
    p = api.post('/api/v1/auth/verify-email', { token }).then(() => undefined);
    inflight.set(token, p);
  }
  return p;
}

function VerifyEmailPage() {
  const { token } = Route.useSearch();
  const qc = useQueryClient();
  const [status, setStatus] = useState<Status>(token ? 'pending' : 'error');
  const [errorMessage, setErrorMessage] = useState('El enlace de verificación está incompleto.');

  useEffect(() => {
    if (!token) return;
    let active = true;
    verifyOnce(token)
      .then(() => {
        if (!active) return;
        setStatus('success');
        void qc.invalidateQueries({ queryKey: ['auth', 'me'] });
      })
      .catch((err: unknown) => {
        if (!active) return;
        setStatus('error');
        setErrorMessage(err instanceof Error ? err.message : 'El enlace es inválido o expiró.');
      });
    return () => {
      active = false;
    };
  }, [token, qc]);

  const authed = isAuthenticated();

  return (
    <div className="grid min-h-screen place-items-center bg-blaze-base px-4 text-foreground">
      <div className="w-full max-w-sm rounded-lg border border-border bg-blaze-elev1 p-6 text-center shadow-xl">
        <img src="/blaze-mark.svg" alt="BlazeIPAM" className="mx-auto mb-3 size-9" />
        {status === 'pending' ? (
          <Result
            icon={<Loader2 className="size-6 animate-spin text-primary" />}
            title="Verificando…"
            body="Un momento, estamos confirmando tu correo."
          />
        ) : status === 'success' ? (
          <Result
            icon={<CheckCircle2 className="size-6 text-[var(--success)]" />}
            title="¡Correo verificado!"
            body="Tu cuenta está confirmada."
            action={
              <Button asChild className="w-full">
                <Link to={authed ? '/' : '/login'}>{authed ? 'Ir al panel' : 'Iniciar sesión'}</Link>
              </Button>
            }
          />
        ) : (
          <Result
            icon={<MailX className="size-6 text-destructive" />}
            title="No se pudo verificar"
            body={errorMessage}
            action={
              <Button asChild variant="outline" className="w-full">
                <Link to="/login">Volver a iniciar sesión</Link>
              </Button>
            }
          />
        )}
      </div>
    </div>
  );
}

function Result({
  icon,
  title,
  body,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  body: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="space-y-3">
      <div className="mx-auto grid size-12 place-items-center rounded-full bg-muted/40">{icon}</div>
      <div className="space-y-1">
        <h1 className="text-base font-semibold">{title}</h1>
        <p className="text-xs text-muted-foreground">{body}</p>
      </div>
      {action}
    </div>
  );
}
